import React, { Component } from 'react'
import ReactDOM from 'react-dom'
import MarkerClusterer from '@google/markerclustererplus'
import m1 from '../images/m1.png'
import mHighlighted from '../images/m-highlighted.png'
import styles from '../styles/map.module.css'

class Map extends Component {
  constructor (props) {
    super(props)
    this.mapDiv = React.createRef()
  }

  componentDidMount () {
    this.prepareMap()
  }

  componentDidUpdate (prevProps) {
    if (prevProps.highlightedStores !== this.props.highlightedStores) {
      this.highlightMarkersAndClusters()
    }

    if (prevProps.selectedStore !== this.props.selectedStore) {
      if (this.props.selectedStore) this.focusOn(this.props.selectedStore)
    }

    if (prevProps.searchResult !== this.props.searchResult) {
      // Update the map with the search result if there is no error and if the map is ready
      if (!this.props.searchResult.error && this.map.getBounds()) {
        this.updateMap(this.props.searchResult)
      }
    }
  }

  prepareMap () {
    this.map = new this.props.google.maps.Map(document.querySelector(`.${styles['map']}`), {
      zoom: window.matchMedia('(max-width: 768px)').matches ? 3 : 4,
      center: {lat: 49.631563, lng: -100.198562},
      mapTypeId: this.props.google.maps.MapTypeId.ROADMAP
    })

    this.infoWindow = new this.props.google.maps.InfoWindow()

    this.map.addListener('mousedown', e => {
      this.infoWindow.close()
      this.props.selectStore(null)
      this.props.enableSearch()
    })

    this.props.google.maps.event.addListenerOnce(this.map, 'idle', e => {
      if (this.props.searchResult && !this.props.searchResult.error) {
        this.updateMap(this.props.searchResult)
      }
    })

    this.generateIcons()
  }

  generateIcons () {
    this.generateMarkers(this.props.google, this.map).then((markers) => {
      this.generateClusters(this.map, markers)
    })
  }
  
  generateMarkers (google, map) {
    this.icon = {
      path: "M24.9,4.2C22,1.4,18.6,0,14.6,0c-4,0-7.5,1.4-10.3,4.2C1.4,7.1,0,10.5,0,14.6c0,2,0.5,4.4,1.6,7c1,2.7,2.2,5.1,3.5,7.2 c1.3,2.1,2.7,4.3,4.4,6.5c1.7,2.2,2.9,3.7,3.5,4.5c0.6,0.7,1.2,1.4,1.6,1.8l1.6-1.7c1-1.2,2.2-2.8,3.6-4.7c1.4-2,2.8-4.1,4.2-6.4 c1.4-2.3,2.7-4.7,3.7-7.3c1-2.6,1.5-4.9,1.5-6.9C29.1,10.5,27.7,7.1,24.9,4.2z",
      fillColor: '#5CAADE',
      fillOpacity: 1,
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(14, 40),
      strokeWeight: 0,
      scale: 0.8
    }

    this.highlightedIcon = {
      path: "M24.9,4.2C22,1.4,18.6,0,14.6,0c-4,0-7.5,1.4-10.3,4.2C1.4,7.1,0,10.5,0,14.6c0,2,0.5,4.4,1.6,7c1,2.7,2.2,5.1,3.5,7.2 c1.3,2.1,2.7,4.3,4.4,6.5c1.7,2.2,2.9,3.7,3.5,4.5c0.6,0.7,1.2,1.4,1.6,1.8l1.6-1.7c1-1.2,2.2-2.8,3.6-4.7c1.4-2,2.8-4.1,4.2-6.4 c1.4-2.3,2.7-4.7,3.7-7.3c1-2.6,1.5-4.9,1.5-6.9C29.1,10.5,27.7,7.1,24.9,4.2z",
      fillColor: '#1A7AB8',
      fillOpacity: 1,
      origin: new google.maps.Point(0, 0),
      anchor: new google.maps.Point(14, 40),
      strokeWeight: 0,
      scale: 0.8
    }

    return new Promise((resolve) => {
      this.markers = this.props.stores.map((store) => {
        const id = store.id;
        const marker = new google.maps.Marker({
          storeID: id,
          position: {
            lat: store.latitude,
            lng: store.longitude
          },
          icon: this.icon,
          map
        })
    
        marker.addListener('click', e => this.props.selectStore(id))
        marker.addListener('mouseover', e => this.props.highlightStores([id]))
        marker.addListener('mouseout', e => this.props.highlightStores([]))
    
        return marker
      })

      this.map.addListener('idle', e => {
        if (this.props.searchResult && !this.props.searchResult.error) {
          const visibleStores = this.props.stores.filter(store => {
            const marker = this.markers.find(marker => marker.storeID === store.id)
            return this.map.getBounds().contains(marker.getPosition())
          })
          
          this.props.updateStores(visibleStores)
        }
      })

      resolve(this.markers)
    })
  }

  generateClusters (map, markers) {
    const clusterStyles = [
      {
        textColor: 'white',
        url: m1,
        height: 40,
        width: 40
      }
    ]

    this.markerCluster = new MarkerClusterer(map, markers, {
      maxZoom: 12,
      styles: clusterStyles
    })

    this.props.google.maps.event.addListener(this.markerCluster, 'mouseover', (cluster) => {
      const storesInCluster = this.props.stores.filter(store => {
        const marker = markers.find(marker => marker.storeID === store.id)
        return cluster.markers_.indexOf(marker) > -1
      }).map(store => {
        return store.id
      })

      this.props.highlightStores([...storesInCluster])
    })

    this.props.google.maps.event.addListener(this.markerCluster, 'mouseout', () => {
      this.props.highlightStores([])
    })

    this.props.google.maps.event.addListener(this.markerCluster, 'click', () => {
      this.props.highlightStores([])
    })
  }

  focusOn (id) {
    const store = this.props.stores.find(store => store.id === id)
    const marker = this.markers.find(marker => marker.storeID === id)

    this.map.setZoom(15)
    this.map.panTo(marker.position)

    this.infoWindow.setContent(this.getTooltipContent(store))
    this.infoWindow.open(this.map, marker)
  }

  boundToClosestStore(bounds, center) {
    // Extend the map to show at least one store while keeping the center point
    const closestStore = this.props.stores.slice().sort((x, y) => x.distance.val - y.distance.val)[0]

    bounds.extend({
      lat: closestStore.latitude,
      lng: closestStore.longitude
    })

    bounds.extend({
      lat: center.lat() - (closestStore.latitude - center.lat()),
      lng: center.lng() - (closestStore.longitude - center.lng())
    })

    this.map.fitBounds(bounds)
  }

  highlightMarkersAndClusters () {
    // Clear all marker highlights
    if (this.highlightedMarker) {
      this.highlightedMarker.setIcon(this.icon)
      this.highlightedMarker = null
    }

    // Clear cluster highlight
    if (this.highlightedCluster) {
      const clusterIcon = this.highlightedCluster.clusterIcon_
      if (clusterIcon.div_ && clusterIcon.div_.firstChild) {
        clusterIcon.div_.firstChild.src = m1
      }

      this.highlightedCluster = null
    }

    // Highlight a cluster if it contains any highlighted store.
    // If not, just highlight the marker.
    if (this.props.highlightedStores.length > 0) {
      const clusters = this.markerCluster.getClusters()

      for (let i = 0; i < this.markers.length; i++) {
        if (this.props.highlightedStores.indexOf(this.markers[i].storeID) > -1) {
          let isInCluster = false
  
          for (let j= 0; j< clusters.length; j++) {
            if (clusters[j].clusterIcon_.visible_ && clusters[j].markers_.length > 0 && clusters[j].markers_.indexOf(this.markers[i]) > -1) {
              const clusterIconDiv = clusters[j].clusterIcon_.div_
  
              if (clusterIconDiv && clusterIconDiv.firstChild) {
                clusterIconDiv.firstChild.src = mHighlighted
                this.highlightedCluster = clusters[j]
              }
  
              isInCluster = true
              break
            }
          }
  
          if (!isInCluster) {
            this.markers[i].setIcon(this.highlightedIcon)
            this.highlightedMarker = this.markers[i]
          }
  
          break
        }
      }
    }
  }

  updateMap (searchResult) {
    const bounds = searchResult.bounds
    const latLng = searchResult.latLng

    if (bounds) {
      let containsStore = false

      for(let i = 0; i < this.markers.length; i++) {
        if (containsStore) {
          break
        } else if (bounds.contains(this.markers[i].getPosition())) {
          containsStore = true
        }
      }

      if (containsStore) {
        this.map.fitBounds(bounds)
      } else {
        this.boundToClosestStore(bounds, latLng)
      }
    } else if (latLng) {
      this.map.setCenter(latLng)
      this.map.setZoom(18)

      let containsStore = false

      for(let i = 0; i < this.markers.length; i++) {
        if (containsStore) {
          break
        } else if (this.map.getBounds().contains(this.markers[i].getPosition())) {
          containsStore = true
        }
      }
  
      if (!containsStore) {
        this.boundToClosestStore(this.map.getBounds(), latLng)
      }
    }
  }

  getTooltipContent (store) {
    const customDiv = document.createElement('div')

    if (store.distance) {
      var startAddress = `${store.distance.from.lat.toString()},${store.distance.from.lng.toString()}`
      var destAddress = `${store.latitude.toString()},${store.longitude.toString()}`
    }

    ReactDOM.render((
      <div className={styles['tooltip']}>
        <div className={`${styles['tooltip__detail']} ${styles['tooltip__detail--bold']}`}>{store.name}</div>
        <div className={styles['tooltip__detail']}>{store.address}</div>
        <div className={styles['tooltip__detail']}>{store.city}, {store.province} {store.postal_code}</div>
        {store.phone && <div className={styles['tooltip__detail']}>tel: <a href={`tel:${store.phone.replace('-', '')}`}>{store.phone}</a></div>}
        {store.distance &&
          <div className={styles['tooltip__detail']}>distance: {store.distance.val}km</div>}
        {store.distance && window.matchMedia('(max-width: 1024px)').matches &&
          <div className={styles['tooltip__detail']}>
            <a target="_blank" rel="noopener noreferrer" href={`https://www.google.com/maps?saddr=${encodeURIComponent(startAddress)}&daddr=${encodeURIComponent(destAddress)}`} className={styles['tooltip__link']}>
              {this.props.lang === 'en'? `directions` : `itinéraire`}
            </a>
          </div>}
      </div>
    ), customDiv)

    return customDiv
  }

  render () {
    return (
      <div className={styles['map']} ref={this.mapDiv}></div>
    )
  }
}

export default Map